import React from "react";
import { useSelector } from "react-redux";
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { formatMoney } from "utils/money";
import { formatStatNumber } from "utils/percentage";

/**
 * Reusable dashboard chart. Recharts stays shared because the dashboard
 * already uses it, and these charts need lines, bars, tooltips, and themes.
 *
 * @param {object} props
 * @param {Array<object>} props.data
 * @param {Array<{dataKey: string, label: string, color: string}>} props.series
 * @param {"line"|"bar"} [props.type="line"]
 * @param {"money"|"count"} [props.valueType="count"]
 * @param {number} [props.height=320]
 */
const ChartComponent = ({ data, series, type = "line", valueType = "count", height = 320 }) => {
    const isDark = useSelector((state) => state.ui.isDark);
    const grid = isDark ? "rgba(255, 255, 255, 0.12)" : "#ccc";
    const axis = isDark ? "#9a9aa4" : "#666";
    const formatValue = valueType === "money" ? formatMoney : formatStatNumber;
    const labelFor = (key) => series.find((item) => item.dataKey === key)?.label || key;
    const compactXAxis = data.length > 6;

    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: compactXAxis ? 18 : 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis
                    dataKey="month"
                    interval={0}
                    stroke={axis}
                    tick={{ fill: axis, fontSize: compactXAxis ? 11 : 13 }}
                    height={compactXAxis ? 42 : 30}
                />
                <YAxis
                    width={valueType === "money" ? 85 : 65}
                    tickFormatter={formatValue}
                    stroke={axis}
                    tick={{ fill: axis }}
                />
                <Tooltip
                    formatter={(value, name) => [formatValue(value), labelFor(name)]}
                    contentStyle={
                        isDark
                            ? {
                                  backgroundColor: "#242424",
                                  border: "1px solid #38393a",
                                  borderRadius: "6px",
                                  color: "#d0d2d6",
                              }
                            : undefined
                    }
                    labelStyle={isDark ? { color: "#d0d2d6" } : undefined}
                    cursor={{ stroke: grid }}
                />
                <Legend formatter={labelFor} />
                {series.map((item) =>
                    type === "bar" ? (
                        <Bar
                            key={item.dataKey}
                            dataKey={item.dataKey}
                            name={item.label}
                            fill={item.color}
                        />
                    ) : (
                        <Line
                            key={item.dataKey}
                            type="monotone"
                            dataKey={item.dataKey}
                            name={item.label}
                            stroke={item.color}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                        />
                    ),
                )}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default ChartComponent;
